import { BoundSelectField, Css, SelectField, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import {
  AddEditItemObjectState,
  CommonFields,
  TakeoffTaskBoundSelectField,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { ReviewSlotRequests_IncomingSlotFragment } from "./ReviewSlotRequests.generated";

type AcceptSlotFormProps = {
  slot: ReviewSlotRequests_IncomingSlotFragment;
  planPackageId: string;
  planPackageVersionId: string;
  formState: ObjectState<AddEditItemObjectState>;
};

export function AcceptSlotForm(props: AcceptSlotFormProps) {
  const { slot, planPackageId, planPackageVersionId, formState } = props;
  // The location/feature dropdown options are display-only and will be set by the backend via `fulfillsPlanPackageIncomingSlotId`
  const placeholder = slot.designPackagePlaceholder;
  const locationOption = { id: "", name: slot.planPackageRpl.location.name };
  const featureOption = placeholder.location.feature
    ? { id: "", name: placeholder.location.feature.displayLocationPath }
    : null;

  const { dimensions, currentMavs } = useComputed(
    () => ({
      dimensions: formState.item.materialAttributeDimensions?.rows?.filter((mad) => mad.useInTakeoff.value),
      currentMavs: formState.materialVariant.value?.materialAttributeValues ?? [],
    }),
    [formState],
  );

  return (
    <div css={Css.df.fdc.gap2.px3.$}>
      {!locationOption && (
        <div css={Css.red600.sm.$}>No suitable ready plan location found for this slot within the plan package.</div>
      )}
      {/* Placeholder type is pre-set by the designer; disabled and not readonly for aesthetics */}
      <BoundSelectField
        label="Placeholder"
        field={formState.itemId}
        options={[placeholder.item]}
        getOptionLabel={(o) => o.displayName}
        getOptionValue={(o) => o.id}
        disabled="Placeholder type cannot be changed"
      />
      {/* TODO: maybe needs a common util? To be shared between THIS-DPModal-TakeoffModall */}
      {/* MAD dimension fields — copies AddEditPlaceholderItemModal */}
      {formState.itemId.value && dimensions?.nonEmpty && (
        <div css={Css.df.fdc.gap1.$}>
          <div css={Css.dg.gtc("30% 30% 30%").gap2.aie.$}>
            {dimensions.map((dimensionState) => {
              const mad = dimensionState.value;
              const unitLabel = mad.unitOfMeasure
                ? ` (${mad.unitOfMeasure?.abbreviation || mad.unitOfMeasure?.name})`
                : "";
              const valueOption = currentMavs.find(({ dimension }) => dimension.id === mad.id);
              const value = valueOption?.id;
              return (
                <div css={Css.df.$} key={mad.id}>
                  <SelectField
                    label={`${mad.name}${unitLabel}`}
                    // Note: Changing of a material attribute would put the data in a weird state where we have a "fulfilled" slot that
                    // will never have a corresponding TLI because the change in MAD creates a separate TLI.
                    // Disabled and not readonly for aesthetics
                    disabled="Material attributes cannot be changed."
                    onSelect={() => {}}
                    unsetLabel="-"
                    options={mad.values?.map((mav) => ({ id: mav.id, name: mav.textValue }))}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    value={value}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
      <SelectField
        label="Location"
        value={locationOption.id}
        options={[locationOption]}
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => o.id}
        onSelect={() => {}}
        disabled
      />
      {featureOption && (
        <SelectField
          label="Feature"
          value={featureOption.id}
          options={[featureOption]}
          getOptionLabel={(o) => o.name}
          getOptionValue={(o) => o.id}
          onSelect={() => {}}
          disabled
        />
      )}
      <TakeoffTaskBoundSelectField
        allowNone
        selectedTask={placeholder.task ?? undefined}
        readOnly={false}
        taskId={formState.taskId}
      />
      <CommonFields
        planPackageId={planPackageId}
        planPackageVersionId={planPackageVersionId}
        readOnly={false}
        formState={formState}
      />
    </div>
  );
}
